import { createContext, useContext, useState, useCallback, ReactNode } from "react";

export interface TourStep {
  target: string; // data-tour attribute value
  title: string;
  description: string;
  route: string; // page the element lives on
  position: "right" | "bottom" | "left" | "top";
}

export const TOUR_STEPS: TourStep[] = [
  {
    target: "sidebar-nav",
    title: "Your navigation",
    description: "Clients, Requests, Templates, Reporting — everything you need is right here.",
    route: "/dashboard",
    position: "right",
  },
  {
    target: "add-client-btn",
    title: "Add your first client",
    description: "Start by adding a client. Just their name and email — takes 30 seconds.",
    route: "/dashboard/clients",
    position: "bottom",
  },
  {
    target: "new-request-btn",
    title: "Create a document request",
    description: "Pick a client and list the documents you need. They'll get a link instantly.",
    route: "/dashboard/requests",
    position: "bottom",
  },

];

const TOUR_KEY = "docuchase_tour_step";
const TOUR_ACTIVE_KEY = "docuchase_tour_active";

interface TourContextValue {
  isActive: boolean;
  currentStep: number;
  startTour: () => void;
  nextStep: () => void;
  prevStep: () => void;
  skipTour: () => void;
  totalSteps: number;
  step: TourStep | null;
}

const TourContext = createContext<TourContextValue | null>(null);

export function useTour() {
  const ctx = useContext(TourContext);
  if (!ctx) throw new Error("useTour must be inside OnboardingTourProvider");
  return ctx;
}

export function OnboardingTourProvider({ children }: { children: ReactNode }) {
  const [isActive, setIsActive] = useState(() => localStorage.getItem(TOUR_ACTIVE_KEY) === "true");
  const [currentStep, setCurrentStep] = useState(() => {
    const saved = localStorage.getItem(TOUR_KEY);
    return saved ? parseInt(saved, 10) : 0;
  });

  const startTour = useCallback(() => {
    setCurrentStep(0);
    setIsActive(true);
    localStorage.setItem(TOUR_ACTIVE_KEY, "true");
    localStorage.setItem(TOUR_KEY, "0");
  }, []);

  const skipTour = useCallback(() => {
    setIsActive(false);
    localStorage.removeItem(TOUR_ACTIVE_KEY);
    localStorage.removeItem(TOUR_KEY);
    localStorage.setItem("docuchase_onboarding_completed", "true");
  }, []);

  const nextStep = useCallback(() => {
    if (currentStep >= TOUR_STEPS.length - 1) {
      skipTour();
      return;
    }
    const next = currentStep + 1;
    setCurrentStep(next);
    localStorage.setItem(TOUR_KEY, String(next));
  }, [currentStep, skipTour]);

  const prevStep = useCallback(() => {
    if (currentStep <= 0) return;
    const prev = currentStep - 1;
    setCurrentStep(prev);
    localStorage.setItem(TOUR_KEY, String(prev));
  }, [currentStep]);

  const step = isActive ? TOUR_STEPS[currentStep] ?? null : null;

  return (
    <TourContext.Provider value={{ isActive, currentStep, startTour, nextStep, prevStep, skipTour, totalSteps: TOUR_STEPS.length, step }}>
      {children}
    </TourContext.Provider>
  );
}
